// Phase 5 C3 — station marker.
//
// Three visual axes per spec C.2 / C.3:
//
//   * **Colour** — `var(--ink-3)` (gray, 0.4 opacity) for sleeping
//     stations; `drColor(median_dr_mm)` (the heatmap HSL palette
//     from Phase 6.1) for stations with clean data. Single source
//     of truth — `drColor` is imported directly so future palette
//     tweaks land on both surfaces at once.
//   * **Size** — diameter scales by `done_with_residual`:
//     0 / 1-3 / 4-9 / 10+ → 8 / 12 / 14 / 16 px.
//   * **Active-now ring** — for stations with at least one
//     SUBMITTED job, a secondary `<circle>` renders behind the
//     marker with a CSS @keyframes pulse animation. Inlined into
//     the SVG (via DivIcon) rather than a separate Leaflet layer
//     to keep z-ordering local to the marker.
//
// Implementation note. CircleMarker (Leaflet primitive) is the
// natural fit for the dot + colour + size axes but doesn't admit
// CSS animations cleanly — its DOM is a single SVG path inside
// the shared overlay pane. We use a `DivIcon`-wrapped `Marker`
// instead so the per-station HTML can carry the pulse-ring
// `<div>` and the dot together. This trades a few microseconds
// of rendering cost for a stable cross-platform pulse animation
// (per spec C3 fallback note — WebKit in Telegram WebView can be
// finicky about SVG-only animations).

import L from 'leaflet';
import { useMemo } from 'react';
import { Marker } from 'react-leaflet';

import type { MapStation } from '../../api/types';
import { drColor } from '../analytics/HeatmapStationConfig';

import './StationMarker.css';

/** Diameter in CSS pixels. Spec C.3 — four bands keyed by clean
 *  done-with-residual count. Half-step values picked so adjacent
 *  bands read visibly different on Telegram's typical 360-px
 *  viewport. */
export function markerDiameter(doneWithResidual: number): number {
  if (doneWithResidual >= 10) return 16;
  if (doneWithResidual >= 4) return 14;
  if (doneWithResidual >= 1) return 12;
  return 8;
}

/** Fill colour. Sleeping → muted gray + 0.4 opacity. With clean
 *  data → the heatmap palette via `drColor`. With jobs but no
 *  clean residual (only polluted DONE) → same gray treatment,
 *  since the median is `null` and we have no signal to colour by.
 *
 *  Phase G.1 — when one or more configs are selected, recolor by
 *  the per_config slice (see `markerColorForSelection`); when no
 *  config is selected fall through to the pre-G.1 station median. */
export function markerColor(station: MapStation): {
  fill: string;
  opacity: number;
} {
  if (station.total_jobs === 0 || station.median_dr_mm === null) {
    return { fill: 'var(--ink-3)', opacity: 0.4 };
  }
  return { fill: drColor(station.median_dr_mm), opacity: 0.9 };
}

/** Phase G.1 — per-config recolor.
 *
 *  Given a set of selected configs (empty = «все»), pick the
 *  colour for this station's marker. Single-config selection is
 *  the canonical case; multi-config uses min(median) over the
 *  subset (best across what the operator chose). Three branches:
 *
 *    * any selected config has `median_dr_mm !== null` →
 *      drColor(min(medians)) — best result wins, classic palette.
 *    * every selected config that exists on this station has
 *      `had_attempt && median === null` → solid black: «пробовали,
 *      strict-clean residual не получилась» (compute_failed /
 *      нет эталона / polluted-cohort dropped everything).
 *    * none of the selected configs exists on this station →
 *      muted gray + 0.4 opacity: «этот срез к станции не относится».
 *
 *  Reuse `markerColor` for the empty-selection case so the «все»
 *  path stays identical to pre-G.1 behaviour. */
export function markerColorForSelection(
  station: MapStation,
  selectedConfigs: string[],
): { fill: string; opacity: number } {
  if (selectedConfigs.length === 0) return markerColor(station);
  // Project the per_config breakdown onto the selected set. Configs
  // the station never saw drop out entirely (treated as «not relevant»
  // for the gray-vs-black decision below).
  const pcMap = new Map<string, (typeof station.per_config)[number]>();
  for (const pc of station.per_config) pcMap.set(pc.config, pc);
  const intersect = selectedConfigs
    .map((c) => pcMap.get(c))
    .filter((pc): pc is (typeof station.per_config)[number] => pc !== undefined);
  if (intersect.length === 0) {
    // No selected config has any history on this station — gray.
    return { fill: 'var(--ink-3)', opacity: 0.4 };
  }
  const numericMedians = intersect
    .map((pc) => pc.median_dr_mm)
    .filter((v): v is number => v !== null);
  if (numericMedians.length > 0) {
    // Min across the projection — best of the selected slice wins,
    // matches Phase 6.5's «лучший» convention.
    return { fill: drColor(Math.min(...numericMedians)), opacity: 0.9 };
  }
  // No measurable residual on any selected config, but at least one
  // had an attempt → black: «пробовали, не получилось».
  return { fill: '#000', opacity: 0.9 };
}

interface Props {
  station: MapStation;
  /** Click target — passed up to MapScreen where the bottom-sheet
   *  selection lives. The marker itself doesn't carry the sheet
   *  state to keep this component dumb. */
  onClick: (station: MapStation) => void;
  /** C5 hook — opacity multiplier for filter rendering. Passing
   *  `0` hides the marker visually without unmounting it (spec
   *  E.3 — opacity-based filtering avoids re-render churn).
   *
   *  Phase G.1 — the config filter no longer hides markers; it
   *  recolors them. This prop survives because the «только с
   *  задачами» toggle still hides sleeping stations. */
  opacityMultiplier?: number;
  /** Phase 5.1 — when true, an accent-colored ring renders behind
   *  the dot to mark the station as selected in BatchModal's
   *  «выбрать на карте» picker. Compatible with `active_now`:
   *  a station can carry both rings simultaneously (pulse +
   *  selection); the DivIcon HTML stacks them with the pulse
   *  ring outermost. */
  isSelected?: boolean;
  /** Phase G.1 — selected GNSS configs from the map filter panel.
   *  Empty array = «все» (no config selected → pre-G.1 station-
   *  median palette). With one or more configs, the marker recolors
   *  via `markerColorForSelection` per spec §G.1: gray = «нет здесь»,
   *  black = «пробовали, не вышло», colour = «есть результат». */
  selectedConfigs?: string[];
}

export function StationMarker({
  station,
  onClick,
  opacityMultiplier = 1,
  isSelected = false,
  selectedConfigs = [],
}: Props) {
  const diameter = markerDiameter(station.done_with_residual);
  const { fill, opacity } = markerColorForSelection(station, selectedConfigs);
  const finalOpacity = opacity * opacityMultiplier;

  // Build the `DivIcon` HTML. The pulse ring sits behind the dot
  // and only renders when `active_now`, so a sleeping station
  // doesn't pay the animation cost. Inline styles for fill /
  // diameter keep the SVG self-contained — no per-station CSS
  // class generation.
  const icon = useMemo(() => {
    const pulseMarkup = station.active_now
      ? `<span class="station-marker-pulse-ring" style="
           width:${diameter + 6}px;height:${diameter + 6}px;
           top:${-3}px;left:${-3}px;
         "></span>`
      : '';
    // Phase 5.1 — selection ring. Solid accent border, no
    // animation. Sits between the pulse ring (if any) and the
    // dot; sized to overlap the dot slightly inwards on each
    // side. Inline width/height/top/left mirror the pulse-ring
    // pattern so the math stays in one place.
    const selectionMarkup = isSelected
      ? `<span class="station-marker-selection-ring" style="
           width:${diameter + 8}px;height:${diameter + 8}px;
           top:${-4}px;left:${-4}px;
         "></span>`
      : '';
    return L.divIcon({
      // Wrapper sized to the dot itself; the pulse ring overflows
      // into negative offsets via the inline `top`/`left`. Keeps
      // the click target tightly on the visible dot.
      iconSize: [diameter, diameter],
      // `iconAnchor` centres the icon on the lat/lng pin.
      iconAnchor: [diameter / 2, diameter / 2],
      className: 'station-marker-icon',
      html: `
        ${pulseMarkup}
        ${selectionMarkup}
        <span class="station-marker-dot" style="
          width:${diameter}px;height:${diameter}px;
          background:${fill};opacity:${finalOpacity};
        "></span>
      `,
    });
  }, [diameter, fill, finalOpacity, station.active_now, isSelected]);

  return (
    <Marker
      position={[station.latitude, station.longitude]}
      icon={icon}
      eventHandlers={{
        click: () => onClick(station),
      }}
      // Skip rendering when fully transparent — keeps the DOM
      // small when most markers are filter-hidden.
      opacity={finalOpacity === 0 ? 0 : 1}
    />
  );
}
